/* eslint-disable no-unsafe-optional-chaining */
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { paths } from 'src/routes/paths';
import { useBannersStore } from 'src/store/banners/banners.store';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { SLIDER_BANNERS_ID } from 'src/config-global';
import { isString } from 'lodash';

export type urlType = {
  urlName: string;
  urlToRedirect: string;
};
export interface DefaultValues {
  name: string;
  text?: string;
  url: urlType[];
  recomendedSize: string;
}

export default function useBannerDetails() {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { getBannerDetails, updateBanner, bannerDetails, isLoadingBannerDetails } =
    useBannersStore();

  const defaultValues = useMemo(
    () => ({
      name: bannerDetails?.name || '',
      recomendedSize: bannerDetails?.recomendedSize || '',
      url: bannerDetails?.url || [],
      text: bannerDetails?.text || '',
      redirectLink: bannerDetails?.url[0].urlToRedirect || '',
    }),
    [bannerDetails]
  );

  useEffect(() => {
    if (id) {
      getBannerDetails(id);
    }
  }, [id]);

  const bannerEditSchema = Yup.object().shape({
    name: Yup.string().required('Campo obrigatório!'),
    url: Yup.mixed(),
    text: Yup.string(),
    redirectLink: Yup.string(),
  });

  const methods = useForm({
    resolver: yupResolver(bannerEditSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      const updatedData = [
        {
          _id: bannerDetails?._id ?? null,
          name: data.name,
          recomendedSize: data.recomendedSize,
          text: data.text,
          url: data.url.map((item: any, index: number) => {
            if (isString(item.urlName)) {
              return {
                ...item,
                urlToRedirect: data.redirectLink || '',
              };
            }
            return {
              urlName: item,
              urlToRedirect: data.redirectLink || '',
            };
          }),
        },
      ];

      await updateBanner(updatedData);
      enqueueSnackbar('Banner atualizado com sucesso!');
      navigate(paths.dashboard.banners);
    } catch (error) {
      console.error(error);
    }
  });

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [bannerDetails, reset, defaultValues]);

  const handleDropUrl = useCallback(
    (acceptedFiles: any) => {
      const images = values.url || [];

      if (bannerDetails && SLIDER_BANNERS_ID.includes(bannerDetails._id ?? '')) {
        const processedFiles = (acceptedFiles || []).map((file: any) => ({
          ...file,
          preview: URL.createObjectURL(file),
        }));

        setValue('url', [...images, ...processedFiles]);
      } else {
        setValue('url', [
          ...acceptedFiles?.map((file: any) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          ),
        ]);
      }
    },
    [setValue, values.url, bannerDetails]
  );

  const handleRemove = (fileToRemove: string | File) => {
    if (!values.url) return;

    const filteredItems = values.url.filter((item: any) =>
      typeof item === 'string' ? item !== fileToRemove : item.name !== (fileToRemove as File).name
    );

    setValue('url', filteredItems);
  };

  const handleRemoveAll = () => {
    setValue('url', []);
  };

  return {
    // permission
    isLoadingBannerDetails,
    isSubmitting,
    methods,
    handleDropUrl,
    handleSubmit,
    onSubmit,
    reset,
    watch,
    setValue,
    handleRemove,
    handleRemoveAll,
  };
}
